import React, { useState, Fragment } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { SNETTextfield, alertTypes, TagsInputField } from "shared/dist/components";
import ServiceIdAvailability from "../ServiceIdAvailability";

import { aiServiceDetailsActions, loaderActions } from "../../../../Services/Redux/actionCreators";
import validator from "shared/dist/utils/validator";

import { serviceIdAvailability } from "../../constant";
import {
    serviceNameFieldsData,
    serviceDescriptionFieldsData,
    serviceAdditionalInfoFieldsData,
} from "./dataBasicServiceDetails";
import { serviceProfileValidationConstraints } from "../validationConstraints";
import { isEmpty } from "lodash";
import StorageTypeSelecting from "../../../../Components/StorageTypeSelecting";

const nameField = serviceNameFieldsData.name;
const idField = serviceNameFieldsData.newId;
let timeout = null;
const TIMEOUT_TINE = 1000;

const BasicServiceDetails = () => {
    const {
        name,
        newId,
        id,
        availability,
        shortDescription,
        longDescription,
        tags,
        storageProvider,
        projectURL,
        contributors,
    } = useSelector(
        (state) => ({
            name: state.aiServiceDetails.name,
            newId: state.aiServiceDetails.newId,
            id: state.aiServiceDetails.id,
            availability: state.aiServiceDetails.availability,
            shortDescription: state.aiServiceDetails.shortDescription,
            longDescription: state.aiServiceDetails.longDescription,
            tags: state.aiServiceDetails.tags,
            storageProvider: state.aiServiceDetails.storageProvider,
            projectURL: state.aiServiceDetails.projectURL,
            contributors: state.aiServiceDetails.contributors,
        }),
        shallowEqual
    );

    const additionalData = {
        shortDescription,
        longDescription,
        projectURL,
        contributors,
    };
    const dispatch = useDispatch();
    const { orgUuid } = useParams();

    const [validationErrors, setValidationErrors] = useState({
        name: undefined,
        shortDescription: undefined,
        longDescription: undefined,
        newId: undefined,
    });

    const handleServiceDetailsLeafChange = (name, value) => {
        dispatch(aiServiceDetailsActions.setAiServiceDetailLeaf(name, value));
    };

    const addTags = (tags) => {
        const lowerCaseTags = tags.map((tag) => tag.toLowerCase());
        handleServiceDetailsLeafChange("tags", lowerCaseTags);
    };

    const validationServiceId = async (serviceId) => {
        // Call the API to Validate the Service Id
        try {
            const serviceAvailability = await dispatch(
                aiServiceDetailsActions.validateServiceId(orgUuid, serviceId)
            );
            dispatch(aiServiceDetailsActions.setIdServiceAvailability(serviceAvailability));
        } catch (error) {
            dispatch(aiServiceDetailsActions.setIdServiceAvailability(""));
        }
    };

    const handleValidation = (fieldName, value) => {
        const invalidMessage = validator.single(
            value,
            serviceProfileValidationConstraints[fieldName]
        );
        if (invalidMessage) {
            setValidationErrors({
                [fieldName]: {
                    type: alertTypes.ERROR,
                    message: invalidMessage[0], // invalidMessage is single-element array
                },
            });
        } else {
            setValidationErrors({ [fieldName]: undefined });
        }
        return isEmpty(invalidMessage);
    };

    const handleControlChange = (event) => {
        const { name, value } = event.target;
        handleValidation(name, value);
        handleServiceDetailsLeafChange(name, value);
    };

    const handleIdChange = (event) => {
        const { name, value } = event.target;
        handleServiceDetailsLeafChange(name, value.replace(/\s/g, ""));
        clearTimeout(timeout);

        if (!handleValidation(name, value)) {
            dispatch(loaderActions.stopValidateServiceIdLoader());
            dispatch(aiServiceDetailsActions.setIdServiceAvailability());
            return;
        }
        dispatch(loaderActions.startValidateServiceIdLoader());

        timeout = setTimeout(async () => {
            await validationServiceId(value);
        }, TIMEOUT_TINE);
    };

    return (
        <Fragment>
            <SNETTextfield
                key={nameField.name}
                name={nameField.name}
                label={nameField.label}
                maxCount={nameField.maxCount}
                description={nameField.description}
                value={name ?? ""}
                onChange={handleControlChange}
                error={Boolean(validationErrors[nameField.name])}
                required
            />
            <SNETTextfield
                key={idField.name}
                name={idField.name}
                label={idField.label}
                maxCount={idField.maxCount}
                description={idField.description}
                value={newId ?? ""}
                onChange={handleIdChange}
                error={
                    (availability && availability !== serviceIdAvailability.AVAILABLE) ||
                    Boolean(validationErrors[idField.name])
                }
                disabled={Boolean(id)}
                required
            />
            {newId !== id && <ServiceIdAvailability id={newId ?? ""} availability={availability} />}
            {serviceDescriptionFieldsData.map((field) => (
                <SNETTextfield
                    fullWidth
                    key={field.name}
                    infoMsg={field.infoMessage}
                    name={field.name}
                    label={field.label}
                    maxCount={field.maxCount}
                    minRows={field.rowCount}
                    maxRows={field.rowCount}
                    inputProps={{ maxLength: field.maxCount }}
                    value={additionalData[field.name]}
                    onChange={handleControlChange}
                    error={validationErrors[field.name]}
                    required={field.required}
                    multiline
                />
            ))}
            <TagsInputField
                addTags={addTags}
                label="Input Tags"
                extraInfo="Insert multiple items separated by comma, hit enter to add"
                tagsEntered={tags}
                additionalInfo="You can add up to 20 tag items"
            />
            {serviceAdditionalInfoFieldsData.map((field) => (
                <SNETTextfield
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    infoMsg={field?.infoMessage}
                    maxCount={field.maxCount}
                    description={field.description}
                    value={additionalData[field.name]}
                    onChange={handleControlChange}
                    error={validationErrors[field.name]}
                    required={field.required}
                />
            ))}
            <StorageTypeSelecting
                storageProvider={storageProvider}
                setStorageType={handleControlChange}
                setLighthouseToken={handleControlChange}
            />
        </Fragment>
    );
};

export default BasicServiceDetails;
